import React, {useEffect, useMemo, useState} from 'react';
import {
  dialogueAsyncActions,
  getDialogue, getDialogueParticipant,
  getTranslations
} from '../../redux/pageAsyncActions';
import {FormattedMessage, useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {IStore} from '../../redux/interface';
import {Link, useParams} from 'react-router-dom';
import {ButtonVariant, GritxButton} from '@wholesalechange/chatcomponent';

import './styles.scss';
import {DialogueChat} from '../../components/dialogue-chat/DialogueChat';
import {clearParticipantErrorAction, dialogueClearedAction} from '../../redux/dialogue/actions';
import NotFoundImg from '../../assets/image/common/notFound.png';
import NotFoundInfo from '../../components/not-found-info';
import {Pages, TranslationKeys} from '../../utils/constants/routeConstants';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';

export const Dialogue: React.FunctionComponent = () => {
  const {
    userProfile, settings
  } = useSelector((state: IStore) => state.auth);
  const {
    dialogue, participant, participantError
  } = useSelector((state: IStore) => state.dialogue);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const intl = useIntl();
  const dispatch = useDispatch();
  const {dialogueId} = useParams();
  const [isShowParticipants, setShowParticipants] = useState<boolean>(false);
  const [isParticipantNotFound, setParticipantNotFound] = useState<boolean>(false);

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Dialogues.toLowerCase());

    return page?.translationKey || TranslationKeys.Dialogues;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Dialogues.toLowerCase());

    return page?.title || Pages.Dialogues;
  }, [settings]);

  function onShowParticipantsPanel() {
    setShowParticipants(!isShowParticipants);
  }

  function handleHideParticipants() {
    setShowParticipants(false);
  }

  useEffect(() => {
    if (dialogueId) {
      dispatch(dialogueAsyncActions[getDialogue](Number(dialogueId)));
    }

    return () => {
      dispatch(dialogueClearedAction());
    };
  }, [dialogueId]);

  useEffect(() => {
    if (dialogue && userProfile) {
      dispatch(dialogueAsyncActions[getDialogueParticipant](userProfile.id, dialogue.id));
    }
  }, [userProfile, dialogue]);

  useEffect(() => {
    dispatch(clearParticipantErrorAction());
  }, []);

  useEffect(() => {
    if (participantError) {
      setParticipantNotFound(true);
    }
  }, [participantError]);

  function renderBreadcrumbs() {
    return <div className="breadcrumbs">
      <Link className="breadcrumbs__previous" to="/Dialogues">
        <FormattedMessage
          id={`gritx.common.navigation.${title}`}
          defaultMessage={title}
        />
      </Link> - <span className="breadcrumbs__current">
        {dialogue?.name}
      </span>
    </div>;
  }

  function renderDialoguePanel() {
    return !loading.includes(getTranslations) && participant && <>
      {renderBreadcrumbs()}
      <div className="dialogue__header">
        <GritxButton
          title={intl.formatMessage({
            id: 'gritx.dialogue.participants',
            defaultMessage: 'Participants'
          })}
          className={'dialogue__button'}
          variant={ButtonVariant.Primary}
          onClick={onShowParticipantsPanel}
        />
      </div>
      <DialogueChat dialogue={dialogue}
        participant={participant}
        isShowParticipants={isShowParticipants}
        onHideParticipants={handleHideParticipants}/>
    </>;
  }

  return <div className="dialogue">
    {renderDialoguePanel()}
    {isParticipantNotFound
      && <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.dialogue.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={NotFoundImg}
      />
    }
  </div>;
};
